/**
 * The known-good caves the generator falls back on when ten draws in a row fail (docs/spec/03 §4):
 * fixed designs with no creatures and nothing loose near the inbox, numbered for the depth.
 */

import type { CaveDef } from '../types';
import type { Draft } from './finish';
import { finishCave } from './finish';
import { quotaShare } from './plan';

const NORMAL: readonly string[] = [
  'WWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWW',
  'W.......r....d......r.......d....r.....W',
  'W.P.........r.....d......r.......d.r...W',
  'W....d.........rrrrrr......r...........W',
  'W..r......d....wwwwww...d.......r...d..W',
  'W.......r.............r......d.........W',
  'W..d.......r...d...........wwwwwww..r..W',
  'W.....wwwwwww.......r......wd.d.dw.....W',
  'W..r..wd.d.dw...d.......r..wd.d.dw..d..W',
  'W.....w.....w.......d......w.....w.....W',
  'W..d..www.www....r.....r...www.www..r..W',
  'W.........r.....d......d...........d...W',
  'W...r.....d....r....rrrrrrr....r.......W',
  'W.d...........d.....wwwwwww......d..r..W',
  'W......r...d..........d.d.....r........W',
  'W..d.......................r......d....W',
  'W.....r......d.....r..........d........W',
  'W..r.....rrrrr.........d.....r.....r...W',
  'W.......dwwwwwd....r.......d.........d.W',
  'W...d..............d.....r.........r.X.W',
  'W.....r.....d...........d.....d........W',
  'WWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWW',
];

const BONUS: readonly string[] = [
  'WWWWWWWWWWWWWWWWWWWW',
  'W..........d...r...W',
  'W.P...d.......d....W',
  'W.......rrrr....d..W',
  'W..d....wwww..r....W',
  'W....d......d....d.W',
  'W..r....d.....r....W',
  'W......d..rrr.....dW',
  'W..d......www...d..W',
  'W....r..d.....d..X.W',
  'W.d.......d.....r..W',
  'WWWWWWWWWWWWWWWWWWWW',
];

/** True if the cave is one of the fallback designs: for tests and diagnostics. */
export function isFallback(def: CaveDef): boolean {
  const map = def.map.join('\n');
  return map === NORMAL.join('\n') || map === BONUS.join('\n');
}

/** The fallback for this depth: a 40 x 22 cave, or the 20 x 12 bonus design. */
export function fallbackCave(id: string, depth: number, bonus: boolean, seed: number): CaveDef {
  const draft: Draft = {
    id,
    name: bonus ? 'THE LITTLE ROOM' : 'THE OLD ROAD',
    theme: undefined,
    colors: [8, 12, 1],
    map: bonus ? BONUS : NORMAL,
    bonus,
    amoebaTime: 0,
    magicWallTime: 0,
    share: quotaShare(depth),
  };
  const def = finishCave(draft, depth, seed);
  if (def) return def;
  // Unreachable while the designs above hold enough diamonds; a plain quota keeps the type honest.
  return {
    id,
    name: draft.name,
    hint: 'Dig for diamonds and find the exit.',
    intermission: bonus,
    colors: draft.colors,
    diamondValue: 10,
    extraValue: 15,
    levels: [{ seed, diamonds: 5, time: 150 }],
    map: draft.map,
  };
}
